import React, {useState, useEffect, useContext, useCallback} from "react";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import { Box, Button, Container } from "@amzn/awsui-components-react";
import ErrorAlert from "../common/ErrorAlert";
import { ExecutionsTable } from "./table/executionsTable";
import { OrdersTable } from "./table/ordersTable";
import { RedriveExecutionDetails } from "./components/RedriveExecutionDetails";
import { useRedriveData } from "./hooks/useRedriveData";
import { useRedriveOrdersData } from "./hooks/useRedriveOrdersData";
import { useFileValidation } from "./hooks/useFileValidation";
import { StageSelector } from "./components/StageSelector";
import { FileUploader } from "./components/FileUploader";
import { useExecuteRedrive } from "./hooks/useExecuteRedrive";
import {StageContext} from "../../contexes/OrderIdContext";
import { Stage } from "../../lusca-search-api/generated-src";

interface Props {
  setStage: React.Dispatch<React.SetStateAction<Stage>>;
}

export default function OrderRedriveContent({ setStage }: Props) {
  const stage = useContext(StageContext);
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Keep selection in sync with the URL, including browser back/forward.
  useEffect(() => {
    const syncFromUrl = () => {
      const urlParams = new URLSearchParams(window.location.search);
      setSelectedExecutionId(urlParams.get('executionId'));
    };
    syncFromUrl();
    window.addEventListener('popstate', syncFromUrl);
    return () => window.removeEventListener('popstate', syncFromUrl);
  }, []);

  // Custom hooks
  const { pastRedrives, loading: loadingRedrives, error: fetchError } = useRedriveData(stage);
  const { orders, loading: loadingOrders, error: ordersError, isTruncated, downloadUrl, metadata } = useRedriveOrdersData(stage, selectedExecutionId);
  const {
    file,
    orderCount,
    error: fileError,
    redriveOK,
    handleFileChange,
    validateRedrive,
    setError,
    removeDuplicates
  } = useFileValidation();
  const { executeRedrive: executeRedriveHook } = useExecuteRedrive();

  // Combine errors from all sources
  const error = [fetchError, fileError, ordersError].filter(Boolean).join('; ') || null;

  // Prefer the already-loaded table row so table clicks render instantly; fall
  // back to the API metadata for deep-links not present in the history list.
  const listItem = pastRedrives.find(
    (item) => item.executionId === selectedExecutionId
  );
  const resolvedMetadata = selectedExecutionId
    ? {
        executor: listItem?.executor ?? metadata?.executor,
        status: listItem?.status ?? metadata?.status,
        requestDate: listItem?.executionDate
          ? new Date(listItem.executionDate)
          : metadata?.timestamp
          ? new Date(metadata.timestamp * 1000)
          : undefined,
        orders: listItem?.orderCount
          ? Number(listItem.orderCount)
          : metadata?.orders,
      }
    : null;

  const executeRedrive = async () => {
    if (file) {
      setLoading(true);
      await executeRedriveHook(file, stage, setSelectedExecutionId, setError);
      setLoading(false);
    }
  };

  const handleSelectExecution = useCallback((executionId: string) => {
    setSelectedExecutionId(executionId);
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set('executionId', executionId);
    window.history.pushState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
  }, []);

  // replaceState (not pushState) so browser back doesn't return to the execution.
  const handleBackToRedrives = () => {
    setSelectedExecutionId(null);
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete('executionId');
    window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
  };

  return (
    <SpaceBetween size="xl">
      <div>
        <SpaceBetween size="l">
          {error && <ErrorAlert content={error} header="Redrive Error" />}
          {selectedExecutionId && (
            <Button
              iconName="arrow-left"
              onClick={handleBackToRedrives}
              variant="link"
            >
              Back to all redrives
            </Button>
          )}

          <Container header={<Box variant="h2">Order Redrive Tool</Box>}>
            <SpaceBetween size="m">
              <Box variant="p">
                This tool allows you to redrive orders that have already been
                processed. If an order is marked as healthy, but an error was
                present in an upstream system like FLARE, you will be able to
                reprocess the CBE Generated by lusca with this tool. You can
                upload a CSV file with Order IDs here, or redrive a single
                order on that order&#39;s page.
              </Box>
              {!selectedExecutionId && (
                <StageSelector
                  stage={stage}
                  setStage={setStage}
                />
              )}
            </SpaceBetween>
          </Container>

          {!selectedExecutionId && (
            <Container>
              <SpaceBetween size="m">
                <FileUploader
                  file={file}
                  handleFileChange={handleFileChange}
                  validateRedrive={validateRedrive}
                  redriveOK={redriveOK}
                  removeDuplicates={removeDuplicates}
                  loading={loading}
                />
                {file && redriveOK && (
                  <Button
                    onClick={executeRedrive}
                    variant="primary"
                    disabled={loading}
                    loading={loading}
                  >
                    Execute Redrive
                  </Button>
                )}
              </SpaceBetween>
            </Container>
          )}

          {selectedExecutionId ? (
            <>
              <RedriveExecutionDetails
                metadata={resolvedMetadata}
                loading={!listItem && loadingOrders}
              />
              <OrdersTable
                items={orders}
                loading={loadingOrders}
                executionId={selectedExecutionId}
                stage={stage}
                isTruncated={isTruncated}
                downloadUrl={downloadUrl}
              />
            </>
          ) : (
            <ExecutionsTable
              items={pastRedrives}
              loading={loadingRedrives}
              onSelectExecution={handleSelectExecution}
            />
          )}
        </SpaceBetween>
      </div>
    </SpaceBetween>
  );
}
